import { message } from '@rendora/common/composables/message';
import { PositionCode } from '@rendora/common/entities/analysis_backend';
import { postOrderClientSubscription } from '@rendora/common/entities/order';
import { showDynamicContact } from '@rendora/common/features/contact';
import { showSwitchAccountModal } from '@rendora/common/features/switch-account';
import { getBuriedService } from '@rendora/common/services/buried/index';
import { getI18nService } from '@rendora/common/services/i18n';
import { useRightsStore } from '@rendora/common/store/rights';
import { useUserStore } from '@rendora/common/store/user';
import { BUY_TYPE, IRightsType, ORDER_TYPE, PAY_TYPE } from '@rendora/common/types/enums';
import { isUS } from '@rendora/common/utils/config';
import { trackUserBehavior, UNIQ_BEHAVIOR } from '@rendora/common/utils/report';
import { getPlatformJumpLink } from '@rendora/common/utils/url';
import { sessionSetItem } from '@rendora/common/utils/webStorage';
import Link from '@rendora/design/components/link/Link.vue';
import { storeToRefs } from 'pinia';
import { RIGHTS_TYPES_CONTACT_DOMESTIC, RIGHTS_TYPES_CONTACT_US } from '../../constants';
import { Preference, usePayment } from '../../store';
import { showPaymentModal } from '../pay-modal/index';
import UpgradePersonalEquity from './PersonalRight.vue';

export const upgradeEquitySymbol = Symbol('upgradeEquity');

export const PaymentRedirectUrlKey = 'payment_redirect_url';

type config = {
  targetType?: IRightsType;
  title?: string;
  close?: () => void;
  behavior?: UNIQ_BEHAVIOR;
};
export const showBuyEquityModal = async (config?: config): Promise<void> => {
  const paymentStore = usePayment();
  const userStore = useUserStore();
  const rightsStore = useRightsStore();

  const { targetType, title, close, behavior } = config || {};
  const buriedService = getBuriedService();
  const i18nService = getI18nService();
  const $t = i18nService.t;

  trackUserBehavior(behavior || UNIQ_BEHAVIOR.CLICK_SUBSCRIBE);

  buriedService?.track('youyan_platform_subscribe_purchase', {
    rights_type: targetType,
  });

  const { rightsDisplay } = storeToRefs(rightsStore);
  const { user, isTeamRight } = storeToRefs(userStore);
  const { isUpgrade } = storeToRefs(paymentStore);

  const { getProductImage } = paymentStore;

  if (!userStore.user) return;

  // 检查当前是否是个人版用户，不然需要弹 message 提示切换账号
  if (
    targetType &&
    isTeamRight.value &&
    [IRightsType.PERSONAL_LITE, IRightsType.PERSONAL].includes(targetType)
  ) {
    message.error({
      content: (
        <div>
          {$t('dang-qian-wei-tuan-dui-zhang-hao-ru-xu-gou-mai-ge-ren-ban-tao-can-qing')}{' '}
          <Link type="info" onClick={() => showSwitchAccountModal({})}>
            {$t('qie-huan-zhang-hao')}
          </Link>{' '}
          {$t('jin-hang-gou-mai')}
        </div>
      ),
    });
    return;
  }

  const equityType = targetType || userStore.user.rights_type;

  if (!isUS()) {
    if (RIGHTS_TYPES_CONTACT_DOMESTIC.includes(equityType)) {
      showDynamicContact(PositionCode['有言官网-付费订阅-咨询定制']); // @xi18n-ignore
      return;
    }
  } else {
    if (RIGHTS_TYPES_CONTACT_US.includes(equityType)) {
      showDynamicContact(PositionCode['有言官网-付费订阅-咨询定制']); // @xi18n-ignore
      return;
    }
    if (equityType === IRightsType.TEAM_LIGHT) {
      location.href = getPlatformJumpLink('/subscribe');
      return;
    }
  }

  // 检查是否达到限购
  await paymentStore.checkPurchaseLimit(ORDER_TYPE.CREDITS);

  if (isUS()) {
    const time = paymentStore.preference;

    const right = rightsDisplay.value?.rights.find(
      r => r.rights_type === (equityType || user.value?.rights_type),
    );
    const detail = right?.rights_by_time.find(
      r => r.months === (time === Preference.MONTHLY ? 1 : 12) && r.buy_type === BUY_TYPE.SUBSCRIBE,
    );

    if (!detail || !right) {
      message.error($t('chuang-jian-ding-dan-shi-bai'));
      return;
    }

    try {
      sessionSetItem(PaymentRedirectUrlKey, location.href);

      const res = await postOrderClientSubscription(
        {
          pay_type: PAY_TYPE.STRIPE,
          rights_id: right.id,
          first_name: $t('yong-hu'),
          product_url: getProductImage(targetType || equityType),
          last_name: user.value?.account_name,
          email: user.value?.email_address || '',
          success_url: getPlatformJumpLink(`/paymentResult`),
          cancel_url: getPlatformJumpLink(`/subscribe`),
        },
        {
          autoErrorHandle: true,
        },
      );

      if (res.data?.vendor_redirect_url) {
        location.href = res.data.vendor_redirect_url;
      }
      return;
    } catch (e) {
      console.warn('[Buy] postOrderClientSubscription error', e);
      return;
    }
  }

  const titleRight = userStore.user.rights_name;
  const titleAction = isUpgrade.value ? $t('sheng-ji-tao-can') : $t('xu-fei-tao-can');

  showPaymentModal(
    <UpgradePersonalEquity defaultRightsType={targetType || equityType} />,
    { title: title ? title : `${titleRight}${titleAction}`, close },
    upgradeEquitySymbol,
    {
      maskStyle: {
        background: 'transparent',
      },
    },
  );
};
